import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./LoginPage.css";

const API_BASE = "https://7gqxfqaejf.execute-api.ap-south-1.amazonaws.com/dev";
const API_KEY  = "CgGBBwnnkF3mny9LNTeoo4maOep63jid207q9tc2";

/* Detect type purely by presence of @ or exactly 10 digits */
const looksLikeEmail = (v) => v.includes("@");
const looksLikePhone = (v) => /^\d{10}$/.test(v.replace(/[\s\-()]/g, ""));

const LoginPage = () => {
  const [identifier, setIdentifier] = useState("");  // email or phone
  const [password, setPassword]     = useState("");
  const [identError, setIdentError] = useState("");
  const [passError, setPassError]   = useState("");
  const [generalError, setGeneralError] = useState("");
  const [isLoading, setIsLoading]   = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("auth") || "null");
    if (user?.username && user?.role) navigate("/dashboard");
    else if (user && !user.role) sessionStorage.removeItem("auth");
  }, [navigate]);

  const storeAndRedirect = (username, role, allowedPages) => {
    sessionStorage.setItem("auth", JSON.stringify({ username, role, allowedPages: allowedPages || [] }));
    // admins land on Invoice Grid (their allowed home); superadmins go to dashboard
    navigate(role === "superadmin" ? "/dashboard" : "/invoiceGrid");
  };

  const handleLogin = async () => {
    setIdentError("");
    setPassError("");
    setGeneralError("");

    let valid = true;
    const ident = identifier.trim();

    if (!ident) {
      setIdentError("Email or phone number is required.");
      valid = false;
    } else if (!looksLikeEmail(ident) && !looksLikePhone(ident)) {
      setIdentError("Enter a valid email address or a 10-digit phone number.");
      valid = false;
    }

    if (!password.trim()) {
      setPassError("Password is required.");
      valid = false;
    }

    if (!valid) return;

    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/users`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
          Origin: window.location.origin,
        },
      });

      if (!res.ok) throw new Error("api_error");

      const data = await res.json();
      const list = Array.isArray(data) ? data : (data ? [data] : []);

      // Match by email or phone number
      const cleanPhone = ident.replace(/[\s\-()]/g, "");
      const byEmail    = looksLikeEmail(ident);
      const matched = list.find(u => {
        const userEmail = (u.email || u.emailId || u.email_id || "").toLowerCase();
        const userPhone = String(u.phoneNumber ?? u.phone ?? u.mobile ?? "");
        if (byEmail) return userEmail === ident.toLowerCase();
        return userPhone === cleanPhone;
      });

      if (!matched) {
        setIdentError("No account found with this email / phone number.");
        return;
      }

      // Validate password
      if ((matched.password || "") !== password) {
        setPassError("Incorrect password.");
        return;
      }

      // Check account status
      if ((matched.status || "").toLowerCase() === "inactive") {
        setGeneralError("Your account is inactive. Please contact the administrator.");
        return;
      }

      const displayName = [matched.firstName, matched.lastName].filter(Boolean).join(" ")
        || matched.email || matched.emailId || ident;
      const role = (matched.role || "admin").toLowerCase();

      storeAndRedirect(displayName, role, matched.allowedPages);
    } catch (err) {
      if (err.message === "api_error") {
        setGeneralError("Unable to reach the server. Please try again.");
      } else {
        setGeneralError("Something went wrong. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleLogin();
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <h1>WELCOME HOME</h1>

        <div className="input-group">
          <label htmlFor="USER ID"> USER ID </label>
          <input
            type="text"
            placeholder="Email or Phone Number"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            autoComplete="username"
          />
          {identError && <div className="error-message">{identError}</div>}
        </div>

        <div className="input-group">
          <label htmlFor="PASSWORD"> PASSWORD </label>
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
            autoComplete="current-password"
          />
          {passError && <div className="error-message">{passError}</div>}
        </div>

        {generalError && (
          <div className="error-message" style={{ marginBottom: "12px", textAlign: "center" }}>
            {generalError}
          </div>
        )}

        <button onClick={handleLogin} disabled={isLoading}>
          {isLoading ? "Signing in…" : "LOGIN"}
        </button>
      </div>
    </div>
  );
};

export default LoginPage;

